import { useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateFamilyMember } from '@/hooks/useFamilyMembers';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { PersonAutocomplete } from './PersonAutocomplete';

// ─── Zod-Schema ────────────────────────────────────────────────────────────

const familyMemberFormSchema = z.object({
  childId: z.string().min(1, 'Kind ist erforderlich.'),
  relationType: z.enum(['biological', 'adopted', 'foster', 'step']),
  /** ID des gewählten zweiten Elternteils ('' = alleinerziehend) */
  coParentId: z.string().optional(),
});

type FamilyMemberFormValues = z.infer<typeof familyMemberFormSchema>;

// ─── Labels ────────────────────────────────────────────────────────────────

const RELATION_TYPE_OPTIONS: Array<{ value: FamilyMemberFormValues['relationType']; label: string }> = [
  { value: 'biological', label: 'Biologisch' },
  { value: 'adopted', label: 'Adoptiert' },
  { value: 'foster', label: 'Pflegekind' },
  { value: 'step', label: 'Stiefkind' },
];

const PARTNERSHIP_TYPE_LABELS: Record<string, string> = {
  married: 'verheiratet',
  partnership: 'Partnerschaft',
  divorced: 'geschieden',
  separated: 'getrennt',
};

// ─── Props ─────────────────────────────────────────────────────────────────

/** Möglicher zweiter Elternteil – abgeleitet aus den Beziehungen des Elternteils */
export interface CoParentOption {
  partnerId: string;
  partnerName: string;
  relationshipId: string;
  type: string; // 'married' | 'partnership' | 'divorced' | 'separated'
}

interface FamilyMemberFormProps {
  /** ID des Elternteils, dem ein Kind zugeordnet wird */
  parentId: string;
  /**
   * Mögliche zweite Elternteile (Partner aus den Beziehungen des Elternteils).
   * Bei genau einem Eintrag wird dieser vorausgewählt, bei mehreren erscheint
   * eine Auswahl, damit das Kind dem richtigen Paar (Heirat/Scheidung) zugeordnet
   * wird und die Ahnenlinie vom passenden Symbol ausgeht.
   */
  coParents?: CoParentOption[];
  /** IDs von bereits zugeordneten Kindern (werden ausgeschlossen) */
  existingChildIds?: string[];
  /** Schließt das Modal */
  onClose: () => void;
}

// ─── FamilyMemberForm ──────────────────────────────────────────────────────

/** Sinnvoller Default für den zweiten Elternteil:
 *  - genau ein Partner → dieser
 *  - mehrere Partner, aber genau eine AKTUELLE Beziehung (verheiratet/
 *    Partnerschaft, Ex-Beziehungen ausgenommen) → der aktuelle Partner.
 *  Verhindert, dass bei "geschieden + neu verheiratet" versehentlich
 *  "Alleinerziehend" gespeichert wird und die Ahnenlinie nicht vom
 *  Paar-Symbol ausgeht. Die Auswahl bleibt änderbar. */
function defaultCoParentId(coParents: CoParentOption[]): string {
  if (coParents.length === 1) return coParents[0].partnerId;
  const current = coParents.filter((c) => c.type === 'married' || c.type === 'partnership');
  return current.length === 1 ? current[0].partnerId : '';
}

export function FamilyMemberForm({
  parentId,
  coParents = [],
  existingChildIds = [],
  onClose,
}: FamilyMemberFormProps) {
  const createFamilyMember = useCreateFamilyMember();
  const confirm = useConfirm();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FamilyMemberFormValues>({
    resolver: zodResolver(familyMemberFormSchema),
    defaultValues: {
      childId: '',
      relationType: 'biological',
      coParentId: defaultCoParentId(coParents),
    },
  });

  // ─── Schließen mit Dirty-Guard ────────────────────────────────────────

  const requestClose = useCallback(async () => {
    if (isSubmitting) return;
    if (isDirty) {
      const ok = await confirm({
        title: 'Eingaben verwerfen?',
        message: 'Nicht gespeicherte Änderungen gehen verloren.',
        confirmLabel: 'Verwerfen',
        cancelLabel: 'Weiter bearbeiten',
        variant: 'default',
      });
      if (!ok) return;
    }
    onClose();
  }, [confirm, isDirty, isSubmitting, onClose]);

  // ─── ESC schließt Modal (mit Dirty-Guard) ─────────────────────────────

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') requestClose();
    }
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [requestClose]);

  // ─── Absenden ─────────────────────────────────────────────────────────

  const onSubmit = async (values: FamilyMemberFormValues) => {
    const coParent = coParents.find((c) => c.partnerId === values.coParentId);
    try {
      await createFamilyMember.mutateAsync({
        child_id: values.childId,
        parent1_id: parentId,
        parent2_id: coParent?.partnerId || undefined,
        relationship_id: coParent?.relationshipId || undefined,
        relation_type: values.relationType,
      });
      onClose();
    } catch {
      // Fehler werden durch den Hook behandelt
    }
  };

  // ─── Auszuschließende IDs (Eltern + bestehende Kinder) ───────────────

  const excludeIds = [
    parentId,
    ...coParents.map((c) => c.partnerId),
    ...existingChildIds,
  ];

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={requestClose}>
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Kind hinzufügen</h2>
          <button
            type="button"
            onClick={requestClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Schließen"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Formular */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 px-5 py-4">
          {/* Kind-Autocomplete */}
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Kind <span className="text-red-500">*</span>
            </label>
            <Controller
              name="childId"
              control={control}
              render={({ field }) => (
                <PersonAutocomplete
                  value={field.value || null}
                  onChange={(id) => field.onChange(id ?? '')}
                  placeholder="Kind suchen..."
                  excludeIds={excludeIds}
                  aria-label="Kind"
                />
              )}
            />
            {errors.childId && (
              <p className="mt-1 text-xs text-red-500">{errors.childId.message}</p>
            )}
          </div>

          {/* Zweiter Elternteil (nur wenn das Elternteil Beziehungen hat) */}
          {coParents.length > 0 && (
            <div>
              <label htmlFor="fmf-coParent" className="mb-1 block text-sm font-medium text-slate-700">
                Zweiter Elternteil
              </label>
              <select
                id="fmf-coParent"
                {...register('coParentId')}
                className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900
                           focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              >
                <option value="">— Alleinerziehend (kein zweiter Elternteil) —</option>
                {coParents.map((c) => (
                  <option key={c.relationshipId} value={c.partnerId}>
                    {c.partnerName}
                    {PARTNERSHIP_TYPE_LABELS[c.type] ? ` (${PARTNERSHIP_TYPE_LABELS[c.type]})` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-400">
                Bestimmt, von welchem Paar-Symbol die Abstammungslinie ausgeht.
              </p>
            </div>
          )}

          {/* Beziehungstyp */}
          <div>
            <label htmlFor="fmf-relationType" className="mb-1 block text-sm font-medium text-slate-700">
              Art der Zuordnung
            </label>
            <select
              id="fmf-relationType"
              {...register('relationType')}
              className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900
                         focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              {RELATION_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={requestClose}
              className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors
                         hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                         disabled:opacity-50"
            >
              {isSubmitting ? 'Speichern...' : 'Kind zuordnen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
